import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useChartOfAccounts } from '@/features/financials/hooks';

const TYPE_ORDER = ['expense', 'revenue', 'asset', 'liability', 'equity'] as const;
const TYPE_LABEL: Record<string, string> = {
  expense: 'Expenses',
  revenue: 'Revenue / Income',
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
};

/**
 * Chart-of-Accounts category picker. Lists the active, postable leaf
 * accounts (parents are filtered out the same way the AI category-suggest
 * function does, so you can never post against a locked header) grouped by
 * account type. `value` / `onChange` are the account id (or null for none).
 */
export function CoaCategorySelect({
  value,
  onChange,
  placeholder = 'Select category',
  className,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
}) {
  const { data: coa } = useChartOfAccounts();

  const grouped = useMemo(() => {
    const active = (coa ?? []).filter((a) => a.is_active);
    const lockedIds = new Set<string>();
    for (const a of active) if (a.parent_id) lockedIds.add(a.parent_id);
    const leaves = active.filter((a) => !lockedIds.has(a.id));
    const byType = new Map<string, typeof leaves>();
    for (const a of leaves) {
      const arr = byType.get(a.account_type) ?? [];
      arr.push(a);
      byType.set(a.account_type, arr);
    }
    for (const arr of byType.values()) {
      arr.sort((x, y) => {
        const xc = x.code ?? '';
        const yc = y.code ?? '';
        if (xc && yc && xc !== yc) return xc.localeCompare(yc);
        return x.name.localeCompare(y.name);
      });
    }
    return byType;
  }, [coa]);

  return (
    <Select
      value={value ?? '__none__'}
      onValueChange={(v) => onChange(v === '__none__' ? null : v)}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— No category —</SelectItem>
        {TYPE_ORDER.map((type) => {
          const arr = grouped.get(type);
          if (!arr || arr.length === 0) return null;
          return (
            <SelectGroup key={type}>
              <SelectLabel>{TYPE_LABEL[type] ?? type}</SelectLabel>
              {arr.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.code ? `${a.code} · ${a.name}` : a.name}
                </SelectItem>
              ))}
            </SelectGroup>
          );
        })}
      </SelectContent>
    </Select>
  );
}
